import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import * as PusherPushNotifications from '@pusher/push-notifications-web';
import { environment } from '../../../environments/environment';
import { firstValueFrom } from 'rxjs';
import { ApiResponse } from '../models/notification.model';

@Injectable({
  providedIn: 'root'
})
export class PusherBeamsService {
  private beamsClient: any;
  private isStarted = false;
  private readonly http = inject(HttpClient);

  constructor() {}

  /**
   * Initialize and start Pusher Beams with User ID
   * Following Step 4: startWithUserId() logic
   */
  async start(userId: number | string, authToken: string): Promise<void> {
    if (this.isStarted) {
      // console.log('[PusherBeams-v7] Already started');
      return;
    }

    try {
      const userIdString = userId.toString().trim();
      // console.log(`[PusherBeams-v7] registering: ${userIdString}`);
      
      this.beamsClient = new PusherPushNotifications.Client({
        instanceId: environment.pusherBeamsInstanceId,
      });

      const res: any = await firstValueFrom(
        this.http.get<any>(`${environment.apiUrl}notifications/beams-auth`, {
          headers: { 'Authorization': `Bearer ${authToken}` }
        })
      );
      
      // console.log('[PusherBeams-v8] Raw Auth Response:', res);

      // --- ROBUST TOKEN EXTRACTION ---
      let token: string | null = null;
      if (res) {
        if (typeof res === 'string') {
          token = res;
        } else {
          // Check common locations
          token = res.token || 
                  (res.data && (typeof res.data === 'string' ? res.data : res.data.token)) ||
                  res.jwt ||
                  res.access_token;
        }
      }

      if (!token) {
        // console.error('[PusherBeams-v8] Auth Response Structure:', res);
        throw new Error('Token missing in auth response');
      }

      // --- CLEANING & SANITIZATION ---
      token = token.toString().trim();
      
      // Remove any 'Bearer ' prefix if the backend included it
      token = token.replace(/^Bearer\s+/i, '');
      
      // Remove any surrounding quotes
      token = token.replace(/^["']|["']$/g, '');
      
      // Remove any non-printable or whitespace characters that might have sneaked in
      token = token.replace(/[^\x21-\x7E]/g, '');
      
      // console.log(`[PusherBeams-v8] Token extracted. Length: ${token.length}`);

      // --- DIAGNOSTIC: Validate and Decode JWT ---
      try {
        const parts = token.split('.');
        if (parts.length === 3) {
          // Use a more robust base64url decoding for browser
          const base64Url = parts[1];
          const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
          const jsonPayload = decodeURIComponent(atob(base64).split('').map(function(c) {
            return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
          }).join(''));
          
          const payload = JSON.parse(jsonPayload);
          // console.log('[PusherBeams-v8] Decoded JWT Payload:', payload);
          
          // CRITICAL CHECKS FOR 401 ERRORS:
          const jwtSub = payload.sub?.toString();
          const jwtExp = payload.exp ? new Date(payload.exp * 1000) : null;
          
          // console.log(`[PusherBeams-v8] Comparison Check:`);
          // console.log(` - Client User ID: "${userIdString}"`);
          // console.log(` - JWT "sub" claim: "${jwtSub}"`);
          // console.log(` - JWT "exp" claim: ${jwtExp ? jwtExp.toLocaleString() : 'MISSING'}`);
          
          if (jwtSub !== userIdString) {
            // console.warn(`[PusherBeams-v8] ❌ ID MISMATCH: The "sub" in your JWT does not match the userId. Pusher WILL return 401.`);
          }

          if (jwtExp && jwtExp < new Date()) {
            // console.error(`[PusherBeams-v8] ❌ TOKEN EXPIRED: Your backend is returning an expired Beams token.`);
          }
        } else {
          // console.warn('[PusherBeams-v8] Token is NOT a valid 3-part JWT! Pusher will reject this.');
        }
      } catch (e) {
        // console.warn('[PusherBeams-v8] Failed to decode token payload:', e);
      }

      await this.beamsClient.start();
      
      await this.beamsClient.setUserId(userIdString, {
        fetchToken: async () => {
          // console.log(`[PusherBeams-v8] Sending token to Pusher...`);
          return token;
        }
      });

      this.isStarted = true;
      // console.log(`✅ [PusherBeams-v7] Registration Successful.`);
    } catch (error) {
      // console.error('❌ [PusherBeams-v7] Fatal Error:', error);
      throw error;
    }
  }

  /**
   * Stop Pusher Beams
   */
  async stop(): Promise<void> {
    if (this.beamsClient) {
      try {
        await this.beamsClient.stop();
        this.isStarted = false;
        // console.log('Pusher Beams stopped');
      } catch (error) {
        // console.error('Failed to stop Beams:', error);
      }
    }
  }

  isBeamsStarted(): boolean {
    return this.isStarted;
  }
}
